import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { StoredFile } from '../models/stored-file';
import { StoredFileUploadParams } from '../params/stored-file-upload.params';
import { StoredFileApiService } from './stored-file-api.service';

export type UploadStage = 'intent' | 'uploading' | 'completing' | 'done' | 'error';

export interface UploadProgress {
  fileName: string;
  stage: UploadStage;
  percent: number;
  error?: string;
}

/**
 * Fluxo pré-assinado no navegador (único suportado: o back nunca recebe o binário):
 *   1. POST /files/upload-intents  -> URL + headers assinados (credenciais do S3)
 *   2. PUT direto no S3 com XHR (fora do HttpClient: sem interceptors/Authorization)
 *   3. POST /files/{id}/complete
 */
@Injectable({ providedIn: 'root' })
export class StoredFileUploadService {
  private readonly api = inject(StoredFileApiService);

  readonly progress = signal<UploadProgress | null>(null);

  async upload(file: File, params: StoredFileUploadParams): Promise<StoredFile> {
    const base: StoredFileUploadParams = {
      ...params,
      originalName: params.originalName ?? file.name,
      contentType: params.contentType ?? (file.type || 'application/octet-stream'),
    };

    try {
      this.set(file.name, 'intent', 15);
      const intent = await firstValueFrom(this.api.createUploadIntent({ ...base, sizeBytes: file.size }));

      this.set(file.name, 'uploading', 25);
      await this.putToStorage(intent.uploadUrl, intent.headers, file, file.name);

      this.set(file.name, 'completing', 90);
      const result = await firstValueFrom(this.api.completeUpload(intent.file.id!));

      this.set(file.name, 'done', 100);
      return result;
    } catch (error: any) {
      this.set(file.name, 'error', 0, error?.message ?? String(error));
      throw error;
    }
  }

  private putToStorage(url: string, headers: Record<string, string>, body: Blob, fileName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      for (const [key, value] of Object.entries(headers ?? {})) xhr.setRequestHeader(key, value);

      xhr.upload.onprogress = e => {
        if (e.lengthComputable) this.set(fileName, 'uploading', 25 + Math.round((e.loaded / e.total) * 60));
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
        ? resolve()
        : reject(new Error(`Storage PUT failed (${xhr.status})`));
      xhr.onerror = () => reject(new Error('Storage PUT network error'));
      xhr.send(body);
    });
  }

  private set(fileName: string, stage: UploadStage, percent: number, error?: string): void {
    this.progress.set({ fileName, stage, percent, error });
  }

  clear(): void {
    this.progress.set(null);
  }
}
